import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { HandHeart, Users, Rocket } from '@phosphor-icons/react';
import { productsAPI, formatApiError } from '../../services/api';
import { toast } from 'sonner';

export function CrowdfundSection({ product, productId, isAuthenticated, isOwnProduct, formatCurrency, onContribute, navigate }) {
  const [contributeAmount, setContributeAmount] = useState('');
  const [contributeMessage, setContributeMessage] = useState('');
  const [contributing, setContributing] = useState(false);
  const [backers, setBackers] = useState([]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await productsAPI.getBackers(productId);
        if (mounted) setBackers(data.backers || []);
      } catch (err) { console.error('Failed to load campaign backers:', err); }
    })();
    return () => { mounted = false; };
  }, [productId]);

  const handleContribute = async () => {
    if (!isAuthenticated) { toast.error('Please login to contribute'); navigate('/login'); return; }
    const amount = parseFloat(contributeAmount);
    if (isNaN(amount) || amount <= 0) { toast.error('Enter a valid amount'); return; }
    setContributing(true);
    try {
      const { data } = await productsAPI.contribute(productId, amount, contributeMessage);
      toast.success(`Contributed ${formatCurrency(amount)}!`);
      onContribute({ amount_raised: data.amount_raised, backer_count: data.backer_count });
      setContributeAmount('');
      setContributeMessage('');
      const { data: refreshed } = await productsAPI.getBackers(productId);
      setBackers(refreshed.backers || []);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setContributing(false); }
  };

  const progress = Math.min(100, ((product.amount_raised || 0) / (product.funding_goal || 1)) * 100);

  return (
    <>
      <div className="mb-6">
        {product.funding_reason && (
          <div className="flex items-center gap-2 mb-3">
            <Rocket className="w-4 h-4 text-[#22C55E]" />
            <span className="text-sm text-[#999999]">{product.funding_reason}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-3xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="amount-raised">
            {formatCurrency(product.amount_raised || 0)}
          </p>
          <p className="text-sm text-[#999999]">
            of {formatCurrency(product.funding_goal || 0)} goal
          </p>
        </div>
        <div className="w-full h-3 bg-[#333333] rounded-full overflow-hidden mb-2" data-testid="funding-progress-bar">
          <div className="h-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#22C55E] font-semibold">{Math.round(progress)}% funded</span>
          <span className="text-[#999999] flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {product.backer_count || 0} backer{(product.backer_count || 0) !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {!isOwnProduct && product.available && (
        <div className="space-y-3 mb-6" data-testid="contribute-section">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999] font-medium">$</span>
              <Input type="number" step="1" min="1" placeholder={product.price?.toString() || '25'} value={contributeAmount} onChange={(e) => setContributeAmount(e.target.value)} className="rounded-sm bg-[#1A1A1A] border-[#333333] text-white pl-8 h-12" data-testid="contribute-amount-input" />
            </div>
            <Button onClick={handleContribute} disabled={contributing} className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-6 font-semibold" data-testid="contribute-button">
              <HandHeart className="w-5 h-5 mr-2" />
              {contributing ? '...' : 'Contribute'}
            </Button>
          </div>
          <Input placeholder="Leave a support message (optional)" value={contributeMessage} onChange={(e) => setContributeMessage(e.target.value)} className="rounded-sm bg-[#1A1A1A] border-[#333333] text-white text-sm h-10" data-testid="contribute-message-input" />
        </div>
      )}

      {backers.length > 0 && (
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6" data-testid="backers-card">
          <CardContent className="p-4">
            <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] mb-3">Recent Backers</p>
            <div className="space-y-3">
              {backers.slice(0, 5).map((b) => (
                <div key={b.contribution_id} className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium">{b.backer_name}</p>
                    {b.message && <p className="text-xs text-[#999999] mt-0.5 line-clamp-1">{b.message}</p>}
                  </div>
                  <span className="text-sm text-[#22C55E] font-semibold shrink-0 ml-3">{formatCurrency(b.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
